<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import {
		getExtSensorsSettings,
		getSensorAutomationOptions,
	} from '../../../helpers/ext_sensor_helpers';
	import { valueOutOfRange } from '../../../helpers/utilities';
	import { getNumberInputConfig } from '../../../helpers/number_input_helpers';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
		//Design system components
	import { Select, Input, Button, Text, Alert } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	let waitForResponse = false;

	const id = real_id(device.id),
		ext_temperature_settings: {
			overtemp_act: string;
			undertemp_act: string;
			name: string;
		}[] = getExtSensorsSettings(device, 'temperature'),
		unit = $settingsStore?.ext_sensors?.temperature_unit || 'C',
		min = { C: -55, F: -67 }[unit],
		max = { C: 125, F: 257 }[unit];

	const options = getSensorAutomationOptions(),
		setTempAutomation = async (sensor: any) => {
			let res;
			waitForResponse = true;
			const local_ip = get_local_ip(id);
			if (local_ip) {
				const prefix = device.channels_count > 1 ? `relay${device.channel}_` : '';
				res = await localReq(
					device,
					`${local_ip}/settings/ext_temperature/${sensor.extid}`,
					'post',
					{
						[`${prefix}overtemp_threshold_t${unit}`]: sensor[`overtemp_threshold_t${unit}`],
						[`${prefix}undertemp_threshold_t${unit}`]:
							sensor[`undertemp_threshold_t${unit}`],
						[`${prefix}overtemp_act`]: sensor.overtemp_act,
						[`${prefix}undertemp_act`]: sensor.undertemp_act,
					},
					6000,
				);
			} else {
				res = await postReq(`/device/relay/settings/temperature_actions`, {
					id,
					extid: sensor.extid,
					channel: device.channel,
					[`overtemp_threshold_t${unit}`]: sensor[`overtemp_threshold_t${unit}`],
					[`undertemp_threshold_t${unit}`]: sensor[`undertemp_threshold_t${unit}`],
					overtemp_act: sensor.overtemp_act,
					undertemp_act: sensor.undertemp_act,
				});
			}
			const toastTexts = {
				success: $format('temperature_automation_scc_toast', {
					default: 'Temperature automation is saved!',
				}),
			};
			onSettingsResponse(res, device, toastTexts);
			waitForResponse = false;
		};

	const handleChangeTempUnit = () => {
		// console.log('go to external sensors and open temperature units');
		// push('/');
	};
</script>

{#each ext_temperature_settings as sensor}
	<Text class="mt-m mb-s">{sensor.name}</Text>
	<Text styleType="body-small" class="mb-s"
		>{$format('ext_sensor_over_temperature_value', {
			values: { units: unit },
			default: 'An over-temperature threshold in °{units}:',
		})}</Text
	>
	<Input
		aria-label={$format('input_over_temperature_threshold', {
			default: 'Input Over-temperature threshold',
		})}
		{...getNumberInputConfig()}
		size="m"
		class="mb-s"
		value={sensor[`overtemp_threshold_t${unit}`]}
		icon="fas fa-calculator"
		error={valueOutOfRange(sensor[`overtemp_threshold_t${unit}`], min, max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => {
			sensor[`overtemp_threshold_t${unit}`] = e.target['value'];
		}}
		id="input-n2m7a0n"
	/>

	<Text styleType="body-small" class="mb-s"
		>{$format('ext_sensot_over_temperature_action', {
			default: 'Over-temperature action:',
		})}
	</Text>
	<Select
		aria-label={$format('select_over_temperature_action', {
			default: 'Select Over-temperature action',
		})}
		size="m"
		class="mb-s"
		value={options.find((i) => i.value == sensor.overtemp_act)}
		icon="fas fa-temperature-high"
		{options}
		on:change={(e) => {
			sensor.overtemp_act = e.detail['value'];
		}}
		id="select-cwxdoir"
	/>

	<Text styleType="body-small" class="mb-s">
		{$format('ext_sensor_under_temperature_value', {
			values: { units: unit },
			default: 'Under-temperature threshold in °{units}:',
		})}
	</Text>
	<Input
		aria-label={$format('input_under_temperature_threshold', {
			default: 'Input Under-temperature threshold',
		})}
		{...getNumberInputConfig()}
		size="m"
		class="mb-s"
		value={sensor[`undertemp_threshold_t${unit}`]}
		icon="fas fa-calculator"
		error={valueOutOfRange(sensor[`undertemp_threshold_t${unit}`], min, max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => {
			sensor[`undertemp_threshold_t${unit}`] = e.target['value'];
		}}
		id="input-aradlsl"
	/>

	<Text styleType="body-small" class="mb-s">
		{$format('ext_sensot_under_temperature_action', {
			default: 'Under-temperature action:',
		})}
	</Text>
	<Select
		aria-label={$format('select_under_temperature_action', {
			default: 'Select Under-temperature action',
		})}
		size="m"
		class="mb-s"
		value={options.find((i) => i.value == sensor.undertemp_act)}
		icon="fas fa-temperature-low"
		{options}
		on:change={(e) => {
			sensor.undertemp_act = e.detail['value'];
		}}
		id="select-8sosmkc"
	/>
	<Button
		size="m"
		width="full"
		loading={waitForResponse}
		disabled={valueOutOfRange(sensor[`overtemp_threshold_t${unit}`], min, max) ||
			valueOutOfRange(sensor[`undertemp_threshold_t${unit}`], min, max)}
		on:click={() => setTempAutomation(sensor)}>{$format('button_save', { default: 'Save' })}</Button
	>
{/each}
